import { useRef, useState } from "react";
import {
  Animated,
  PanResponder,
  type GestureResponderHandlers,
  type LayoutChangeEvent,
  type PanResponderInstance,
} from "react-native";

// HR-11: drag-to-reorder for a short vertical list of equal-height rows.
// PanResponder, not gesture-handler: core RN, and RNW implements it for mouse
// and touch. The dragged row follows the finger; the rows it passes step aside by one row height; the drop commits once.

/** Where a row dragged `dy` px from slot `from` lands. */
export function dragTargetIndex(
  from: number,
  dy: number,
  rowHeight: number,
  count: number,
): number {
  if (rowHeight <= 0 || count <= 0) return from;
  return Math.max(0, Math.min(count - 1, from + Math.round(dy / rowHeight)));
}

type Drag = { from: number; to: number };

/** Style for a row while a drag is live; null when it stays put. */
export type RowOffset = {
  transform: { translateY: Animated.Value | number }[];
  zIndex?: number;
} | null;

export function useSortableDrag({
  count,
  onDrop,
}: {
  count: number;
  onDrop: (from: number, to: number) => void;
}) {
  const dy = useRef(new Animated.Value(0)).current;
  const rowH = useRef(0);
  const [drag, setDrag] = useState<Drag | null>(null);
  const live = useRef<Drag | null>(null);
  const cfg = useRef({ count, onDrop });
  cfg.current = { count, onDrop };
  const responders = useRef(new Map<number, PanResponderInstance>());

  const end = (commit: boolean) => {
    const d = live.current;
    live.current = null;
    dy.setValue(0);
    setDrag(null);
    if (commit && d && d.to !== d.from) cfg.current.onDrop(d.from, d.to);
  };

  /** Spread onto the row's drag handle. One responder per slot, built once. */
  const handleProps = (index: number): GestureResponderHandlers => {
    let r = responders.current.get(index);
    if (!r) {
      r = PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: () => true,
        // Keep the drag when the sheet's scroll view asks for the touch.
        onPanResponderTerminationRequest: () => false,
        onPanResponderGrant: () => {
          live.current = { from: index, to: index };
          dy.setValue(0);
          setDrag(live.current);
        },
        onPanResponderMove: (_e, g) => {
          const d = live.current;
          if (!d) return;
          dy.setValue(g.dy);
          const to = dragTargetIndex(d.from, g.dy, rowH.current, cfg.current.count);
          if (to !== d.to) {
            live.current = { from: d.from, to };
            setDrag(live.current);
          }
        },
        onPanResponderRelease: () => end(true),
        onPanResponderTerminate: () => end(false),
      });
      responders.current.set(index, r);
    }
    return r.panHandlers;
  };

  /** Offset for the row in slot `index` while a drag is live. */
  const rowOffset = (index: number): RowOffset => {
    if (!drag) return null;
    if (index === drag.from) return { transform: [{ translateY: dy }], zIndex: 1 };
    const h = rowH.current;
    if (drag.from < drag.to && index > drag.from && index <= drag.to) {
      return { transform: [{ translateY: -h }] };
    }
    if (drag.from > drag.to && index >= drag.to && index < drag.from) {
      return { transform: [{ translateY: h }] };
    }
    return null;
  };

  const onRowLayout = (e: LayoutChangeEvent) => {
    rowH.current = e.nativeEvent.layout.height;
  };

  return {
    handleProps,
    rowOffset,
    onRowLayout,
    draggingIndex: drag?.from ?? null,
  };
}
